'use client';

import { useState, useEffect, useRef, useCallback } from 'react';

/** Shape of each item in the profile_update event */
interface SSEProfileItem {
  identifier: string;
  profileIdentifier: string;
  profileSource: string;
  status: string;
  finalScore: number | null;
  referenceDate: string | null;
  viewedAt: string | null;
  processingStatus: string | null;
  slug: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  summary: string | null;
  originalFilename: string | null;
  evaluationStatus: string | null;
}

/** profile_update event payload */
interface SSEProfileUpdateEvent {
  items: SSEProfileItem[];
  total: number;
}

/** processing_complete event payload */
interface SSEProcessingCompleteEvent {
  allDone: boolean;
}

interface UseSSEUnifiedProfileListOptions {
  jobSlug: string | null;
  enabled?: boolean;
  withPlaceholders?: boolean;
  /** Called every ~2s with the latest profile data */
  onProfileUpdate?: (items: SSEProfileItem[], total: number) => void;
  /** Called when all profiles finished processing */
  onProcessingComplete?: () => void;
}

const MAX_RETRIES = 5;

/**
 * Hook to receive real-time unified profile updates via Server-Sent Events (SSE).
 * Connects to /api/proxy/v1/profile/{jobSlug}/list/events and receives
 * profile_update events every 2 seconds until processing_complete.
 */
export const useSSEUnifiedProfileList = ({
  jobSlug,
  enabled = true,
  withPlaceholders = true,
  onProfileUpdate,
  onProcessingComplete,
}: UseSSEUnifiedProfileListOptions) => {
  const [items, setItems] = useState<SSEProfileItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const eventSourceRef = useRef<any>(null);
  const retryAttemptsRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onProfileUpdateRef = useRef(onProfileUpdate);
  const onProcessingCompleteRef = useRef(onProcessingComplete);

  useEffect(() => { onProfileUpdateRef.current = onProfileUpdate; }, [onProfileUpdate]);
  useEffect(() => { onProcessingCompleteRef.current = onProcessingComplete; }, [onProcessingComplete]);

  const close = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
    setIsProcessing(false);
  }, []);

  useEffect(() => {
    if (!enabled || !jobSlug) {
      return undefined;
    }

    retryAttemptsRef.current = 0;

    const params = new URLSearchParams();
    if (withPlaceholders) {
      params.set('withPlaceholders', 'true');
    }
    const url = `/api/proxy/v1/profile/${jobSlug}/list/events?${params.toString()}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;
    setIsConnected(true);
    setIsProcessing(true);
    setError(null);

    /** profile_update — emitted on connection and every 2s */
    es.addEventListener('profile_update', (event: MessageEvent) => {
      try {
        retryAttemptsRef.current = 0;
        const payload: SSEProfileUpdateEvent = JSON.parse(event.data as string);
        setItems(payload.items ?? []);
        setTotal(payload.total ?? 0);
        onProfileUpdateRef.current?.(payload.items ?? [], payload.total ?? 0);
      } catch {
        // ignore malformed events
      }
    });

    /** processing_complete — all done, server closes connection */
    es.addEventListener('processing_complete', (event: MessageEvent) => {
      try {
        const payload: SSEProcessingCompleteEvent = JSON.parse(event.data as string);
        if (payload.allDone) {
          setIsProcessing(false);
          onProcessingCompleteRef.current?.();
          close();
        }
      } catch {
        // ignore malformed events
      }
    });

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
      setIsConnected(false);

      if (retryAttemptsRef.current < MAX_RETRIES) {
        const delay = Math.min(1000 * 2 ** retryAttemptsRef.current, 30000);
        retryAttemptsRef.current += 1;
        retryTimerRef.current = setTimeout(() => {
          setRetryCount((c) => c + 1);
        }, delay);
      } else {
        setError(new Error('SSE connection failed after multiple retries'));
        setIsProcessing(false);
      }
    };

    return () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
      close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [close, enabled, jobSlug, retryCount, withPlaceholders]);

  return {
    items,
    total,
    isConnected,
    isProcessing,
    error,
    close,
  };
};
